import {MUIBox, MUITypography} from '@/components/MUI-Components';
import IconButton from '@mui/material/IconButton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {SPEED_RATES} from './speechRates';

interface AudioPlayerViewProps {
    isPlaying?: boolean;
    speechRate?: number;
    speechProgress?: number;
    speechDuration?: string;
    canSeek?: boolean;
    onPlayPause?: () => void;
    onProgressClick?: (e: React.MouseEvent<HTMLElement>) => void;
    onSpeedChange?: (rate: number) => void;
}

/**
 * Markup of the text-to-speech player, with no speech logic. `AudioPlayer` drives it with `useSpeech`;
 * rendered with the defaults it is the server-side placeholder that holds the player's exact space
 * while the speech chunk loads, so the views count and share row never move when the player appears.
 */
const AudioPlayerView = ({
    isPlaying = false,
    speechRate = 1,
    speechProgress = 0,
    speechDuration = '',
    canSeek = false,
    onPlayPause,
    onProgressClick,
    onSpeedChange
}: AudioPlayerViewProps) => (
    <MUIBox className="bdn-audio-player">
        <IconButton className="bdn-play-btn" onClick={onPlayPause} aria-label={isPlaying ? 'Pause article' : 'Play article'}>
            {isPlaying ? (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                </svg>
            ) : (
                <PlayArrowIcon />
            )}
        </IconButton>
        <MUITypography className="bdn-listen-label">Listen to article</MUITypography>
        <MUIBox className="bdn-progress-wrap">
            <MUIBox
                className="bdn-progress-bar"
                onClick={onProgressClick}
                role="progressbar"
                aria-valuenow={speechProgress}
                aria-valuemin={0}
                aria-valuemax={100}
                sx={{cursor: canSeek ? 'pointer' : 'default'}}
            >
                <MUIBox className="bdn-progress-fill" style={{width: `${speechProgress}%`}} />
            </MUIBox>
            <MUITypography className="bdn-progress-time">{speechDuration || (isPlaying ? '0:00' : '')}</MUITypography>
        </MUIBox>
        <select
            className="bdn-speed-select"
            value={speechRate}
            onChange={(e) => onSpeedChange?.(Number.parseFloat(e.target.value))}
            aria-label="Playback speed"
        >
            {SPEED_RATES.map((rate) => (
                <option key={rate} value={rate}>
                    {rate}x
                </option>
            ))}
        </select>
    </MUIBox>
);

export default AudioPlayerView;
